import { getExtensionValue, MedplumClient } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";

import {
  fetchAmendmentHistoryByQuestionnaireUrl,
  fetchQuestionnaireResponseHistoryByQuestionnaireResponseId,
} from "@/encounters/services/questionnaire-response";
import { Extensions } from "@/fhir/enums/extensions";
import { joinReferenceString } from "@/utils/medplum";

const AMENDMENT_HISTORY_STATUSES = new Set<QuestionnaireResponse["status"]>([
  "completed",
  "amended",
]);

/**
 * Returns the parent encounter version ID stored on a physical exam response.
 * @param questionnaireResponse Physical exam questionnaire response.
 */
export function getParentVersionId(
  questionnaireResponse: QuestionnaireResponse,
): string | undefined {
  const parentVersionId = getExtensionValue(
    questionnaireResponse,
    Extensions.QuestionnaireResponseParentVersionId,
  );

  return typeof parentVersionId === "string" ? parentVersionId : undefined;
}

/**
 * Deduplicates questionnaire responses by meta.versionId and sorts newest-first.
 * @param resources Questionnaire response versions to normalize.
 */
function dedupeQuestionnaireResponsesByMetaVersionId(
  resources: QuestionnaireResponse[],
): QuestionnaireResponse[] {
  const byMetaVersionId = new Map<string, QuestionnaireResponse>();

  for (const resource of resources) {
    const metaVersionId = resource.meta?.versionId;
    if (!metaVersionId) continue;
    byMetaVersionId.set(metaVersionId, resource);
  }

  return Array.from(byMetaVersionId.values()).sort((left, right) => {
    const leftTime = left.meta?.lastUpdated ? Date.parse(left.meta.lastUpdated) : 0;
    const rightTime = right.meta?.lastUpdated ? Date.parse(right.meta.lastUpdated) : 0;
    return rightTime - leftTime;
  });
}

/**
 * Loads filtered and full physical exam resource histories for amendment diff pairing.
 * @param medplum Medplum client.
 * @param questionnaireUrl Physical exam questionnaire canonical URL.
 * @param encounterId Encounter id.
 */
export async function fetchPhysicalExamHistoriesForDiff(
  medplum: MedplumClient,
  questionnaireUrl: string,
  encounterId: string,
): Promise<{
  examHistories: QuestionnaireResponse[] | undefined;
  fullResourceHistory: QuestionnaireResponse[] | undefined;
  encounterPhysicalExamResponses: QuestionnaireResponse[];
  title: string | undefined;
}> {
  const [filteredHistory, title] = await fetchAmendmentHistoryByQuestionnaireUrl(
    medplum,
    questionnaireUrl,
    encounterId,
  );

  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: questionnaireUrl,
    status: "active",
    _sort: "-_lastUpdated",
  });

  if (!questionnaire?.id) {
    return {
      examHistories: filteredHistory,
      fullResourceHistory: undefined,
      encounterPhysicalExamResponses: [],
      title,
    };
  }

  const encounterPhysicalExamResponses = await medplum.searchResources("QuestionnaireResponse", {
    questionnaire: joinReferenceString("Questionnaire", questionnaire.id),
    encounter: joinReferenceString("Encounter", encounterId),
    _sort: "-_lastUpdated",
    _count: 100,
  });

  const mergedHistoryVersions: QuestionnaireResponse[] = [];

  for (const response of encounterPhysicalExamResponses) {
    if (!response.id) {
      mergedHistoryVersions.push(response);
      continue;
    }

    const resourceHistory = await fetchQuestionnaireResponseHistoryByQuestionnaireResponseId(
      medplum,
      response.id,
    );
    mergedHistoryVersions.push(...resourceHistory);
  }

  const mergedAmendmentHistory = dedupeQuestionnaireResponsesByMetaVersionId(
    mergedHistoryVersions.filter((resource) => AMENDMENT_HISTORY_STATUSES.has(resource.status)),
  );

  const primaryQuestionnaireResponseId =
    filteredHistory?.[0]?.id ?? encounterPhysicalExamResponses[0]?.id;
  const primaryResourceHistory = primaryQuestionnaireResponseId
    ? await fetchQuestionnaireResponseHistoryByQuestionnaireResponseId(
        medplum,
        primaryQuestionnaireResponseId,
      )
    : [];

  const examHistories =
    mergedAmendmentHistory.length > (filteredHistory?.length ?? 0)
      ? mergedAmendmentHistory
      : filteredHistory;

  const fullResourceHistory =
    mergedAmendmentHistory.length > primaryResourceHistory.length
      ? mergedAmendmentHistory
      : primaryResourceHistory;

  return {
    examHistories,
    fullResourceHistory,
    encounterPhysicalExamResponses,
    title,
  };
}

/**
 * Finds the previous physical exam snapshot for an amended version.
 * @param examHistories Filtered physical exam history ordered newest-first.
 * @param amendedPhysicalExamIndex Index of the current amended physical exam response.
 * @param versionId Encounter questionnaire response version being viewed.
 * @param fullResourceHistory Full resource version history ordered newest-first.
 */
function findPreviousPhysicalExamVersion(
  examHistories: QuestionnaireResponse[],
  amendedPhysicalExamIndex: number,
  versionId: string,
  fullResourceHistory?: QuestionnaireResponse[],
): QuestionnaireResponse | undefined {
  const historyPools = [examHistories, fullResourceHistory ?? []];

  for (const historyPool of historyPools) {
    const previousFromDifferentParent = historyPool
      .slice(amendedPhysicalExamIndex + 1)
      .find((questionnaireResponse) => {
        const parentVersionId = getParentVersionId(questionnaireResponse);
        return parentVersionId !== undefined && parentVersionId !== versionId;
      });

    if (previousFromDifferentParent) {
      return previousFromDifferentParent;
    }

    const previousFromAdjacentHistory = historyPool[amendedPhysicalExamIndex + 1];
    if (previousFromAdjacentHistory) {
      return previousFromAdjacentHistory;
    }
  }

  return undefined;
}

/**
 * Finds a physical exam response linked to a specific encounter version.
 * @param physicalExamResponses Physical exam responses to search.
 * @param encounterVersionId Encounter questionnaire response version id.
 */
function findPhysicalExamByEncounterVersionId(
  physicalExamResponses: QuestionnaireResponse[],
  encounterVersionId: string,
): QuestionnaireResponse | undefined {
  return physicalExamResponses.find(
    (questionnaireResponse) => getParentVersionId(questionnaireResponse) === encounterVersionId,
  );
}

/**
 * Finds the physical exam response effective for an encounter version or older versions.
 * @param examHistories Physical exam history ordered newest-first.
 * @param encounterVersionIds Encounter version IDs ordered newest-first.
 * @param startEncounterVersionIndex Index into encounterVersionIds to begin matching.
 */
export function findPhysicalExamForEncounterVersion(
  examHistories: QuestionnaireResponse[],
  encounterVersionIds: string[],
  startEncounterVersionIndex: number,
): QuestionnaireResponse | undefined {
  const validParentVersionIds = new Set(encounterVersionIds.slice(startEncounterVersionIndex));

  return examHistories.find((questionnaireResponse) => {
    const parentVersionId = getParentVersionId(questionnaireResponse);
    return parentVersionId !== undefined && validParentVersionIds.has(parentVersionId);
  });
}

/**
 * Resolves current/previous physical exam responses for an encounter amendment version.
 * @param examHistories Physical exam history ordered newest-first.
 * @param versionId Encounter questionnaire response version being viewed.
 * @param encounterVersionIds Encounter version IDs ordered newest-first.
 * @param fullResourceHistory Full merged resource version history ordered newest-first.
 * @param encounterPhysicalExamResponses Latest physical exam responses for the encounter.
 */
export function resolvePhysicalExamVersionPair(
  examHistories: QuestionnaireResponse[],
  versionId: string,
  encounterVersionIds: string[],
  fullResourceHistory?: QuestionnaireResponse[],
  encounterPhysicalExamResponses: QuestionnaireResponse[] = [],
): {
  currentQuestionnaireResponse: QuestionnaireResponse | undefined;
  previousQuestionnaireResponse: QuestionnaireResponse | undefined;
} {
  const viewingIndex = encounterVersionIds.indexOf(versionId);
  const amendedPhysicalExamIndex = examHistories.findIndex(
    (questionnaireResponse) => getParentVersionId(questionnaireResponse) === versionId,
  );

  if (amendedPhysicalExamIndex !== -1) {
    const currentQuestionnaireResponse = examHistories[amendedPhysicalExamIndex];
    const previousFromHistory = findPreviousPhysicalExamVersion(
      examHistories,
      amendedPhysicalExamIndex,
      versionId,
      fullResourceHistory,
    );
    const previousFromEncounterTimeline =
      viewingIndex !== -1
        ? findPhysicalExamForEncounterVersion(examHistories, encounterVersionIds, viewingIndex + 1)
        : undefined;
    const previousFromMergedHistory =
      viewingIndex !== -1
        ? findPhysicalExamForEncounterVersion(
            fullResourceHistory ?? [],
            encounterVersionIds,
            viewingIndex + 1,
          )
        : undefined;
    const previousFromEncounterResponses =
      viewingIndex !== -1
        ? findPhysicalExamForEncounterVersion(
            encounterPhysicalExamResponses,
            encounterVersionIds,
            viewingIndex + 1,
          )
        : undefined;
    const previousEncounterVersionId =
      viewingIndex !== -1 ? encounterVersionIds[viewingIndex + 1] : undefined;
    const previousFromExactEncounterVersion = previousEncounterVersionId
      ? findPhysicalExamByEncounterVersionId(
          [...examHistories, ...(fullResourceHistory ?? []), ...encounterPhysicalExamResponses],
          previousEncounterVersionId,
        )
      : undefined;

    const previousQuestionnaireResponse =
      previousFromHistory ??
      previousFromExactEncounterVersion ??
      previousFromEncounterResponses ??
      previousFromMergedHistory ??
      previousFromEncounterTimeline;

    return { currentQuestionnaireResponse, previousQuestionnaireResponse };
  }

  const effectivePhysicalExam =
    viewingIndex !== -1
      ? findPhysicalExamForEncounterVersion(examHistories, encounterVersionIds, viewingIndex)
      : examHistories[0];

  return {
    currentQuestionnaireResponse: effectivePhysicalExam,
    previousQuestionnaireResponse: effectivePhysicalExam,
  };
}
